import React from "react";
import type { Dispatch } from "redux";
import Select from "react-select";
import { connect } from "react-redux";
import { sumBy } from "es-toolkit";

import KpiTable from "./KpiTable";
import { ADD_KPI_ACTION_NAME } from "./reducers/kpis";
import { getBackend as getBackend2 } from "../../../lib/backend/Backend2";
import { niceDateTime } from "../../../lib/Util";
import { translate } from "../../IntlProviderWrapper";
import { LINE_GROUP_TAGS_ACCESS, PLANNING_META_OPTIMIZATION } from "../../../lib/ManufacturingTags.generated";
import * as BusinessLogic from "../../../lib/BusinessLogic";
import { LinesOrdersLogic, RecommendPlanLogic } from "./reducers/BusinessLogic";
import { PLANNING_SCHEDULE_STRATEGY } from "../../../lib/ManufacturingConsts.generated";
import {
    UPDATE_CURRENT_RECOMMEND_PLAN,
    UPDATE_INITIAL_RECOMMEND_PLAN,
    SET_PENDING_CURRENT_REPORT_UUID,
    SET_PENDING_INITIAL_REPORT_UUID,
} from "./reducers/recommendPlan";
import Loader from "../../Loader";
import PlanScoresTable from "./PlanScoresTable";
import { SET_RECOMMEND_PLAN_MODAL } from "./reducers/planningTable";

import type { ILabelValuePair } from "../../../lib/Util";
import type { RecommendPlanActions, RecommendPlanParamsAndCriteria } from "./reducers/recommendPlan";
import type { KpisActions } from "./reducers/kpis";
import type { ReduxState } from "./reducers/reducers";
import type { IWeekKPIs } from "../../../lib/backend/reports.generated.types";
import type { RunRecommendPlanParameters } from "./reducers/BusinessLogic";
import type { PlanningTableActionsTypes } from "./reducers/planningTable";

import type { IPlanScore } from "../../../lib/backend/manufacturing2.generated.types";

type ActionTypes = KpisActions | RecommendPlanActions | PlanningTableActionsTypes;

function calcKpiSum(kpis: IWeekKPIs[]): IWeekKPIs {
    return {
        available_hours: sumBy(kpis, kpi => kpi.available_hours),
        delay_operations: sumBy(kpis, kpi => kpi.delay_operations),
        no_input_materials_operations: sumBy(kpis, kpi => kpi.no_input_materials_operations),
        tool_changes_count: sumBy(kpis, kpi => kpi.tool_changes_count),
        tool_changes_hours: sumBy(kpis, kpi => kpi.tool_changes_hours),
        used_hours: sumBy(kpis, kpi => kpi.used_hours),
        worker_hours: sumBy(kpis, kpi => kpi.worker_hours),
        week: -1,
        year: -1,
    };
}

type OwnProps = {
    is_loading: boolean;
    line_group_uuid: string;
};

type Props = OwnProps & ReduxProps;

type State = {
    dropdown2_value: string;
    dropdown1_value: string;
    new_base: boolean;
    loading: boolean;
    parameters: RecommendPlanParamsAndCriteria | null;
    show_modal: boolean;
};

class KpisContainer extends React.Component<Props, State> {
    state: State = {
        dropdown2_value: "",
        dropdown1_value: "",
        loading: false,
        new_base: true,
        parameters: null,
        show_modal: false,
    };

    componentDidMount() {
        this.load();
        this.setState({ loading: this.props.is_loading });
    }

    componentDidUpdate = async (prev_props: Props, prev_state: State) => {
        if (prev_props.is_loading !== this.props.is_loading) {
            this.setState({ loading: this.props.is_loading });
        }

        if (prev_state.dropdown2_value !== this.state.dropdown2_value && this.state.dropdown2_value) {
            this.loadReport(this.state.dropdown2_value, true);
        }

        if (
            prev_state.dropdown1_value !== this.state.dropdown1_value &&
            this.state.dropdown1_value !== this.state.dropdown2_value &&
            this.state.dropdown1_value
        ) {
            this.loadReport(this.state.dropdown1_value);
        }

        if (this.props.initial_recommend_plan === undefined && this.props.pending_initial_report_uuid === "") {
            this.loadInitialPlanScore();
        }

        if (
            prev_props.recommend_plans !== this.props.recommend_plans ||
            prev_props.original_report_uuid !== this.props.original_report_uuid ||
            prev_props.report_uuid !== this.props.report_uuid
        ) {
            this.load();
        }
    };

    prepareRecommendPlanParams = (initial: boolean): RunRecommendPlanParameters => {
        const line_group_uuid = this.props.line_group_uuid;
        const line_group_tags = BusinessLogic.getLineGroupTags(line_group_uuid);
        const original_lines_orders = this.props.original_lines_orders;
        const original_unscheduled_orders = this.props.original_unscheduled_orders || [];
        const lines_orders = initial ? original_lines_orders : this.props.lines_orders;
        const unscheduled_orders = initial ? original_unscheduled_orders : this.props.unscheduled_orders;
        const parameters: RunRecommendPlanParameters = {
            line_group_uuid,
            line_uuids: this.props.selected_line_uuids,
            state: {
                recommend_param_beam_size: 1,
                recommend_param_can_change_workorder_line: true,
                recommend_param_track_input_material:
                    LINE_GROUP_TAGS_ACCESS.planning_track_input_material(line_group_tags),
                recommend_param_use_productions_of_unscheduled_operations: true,
                recommend_param_use_requirements_from_unscheduled_operations: true,
                recommend_param_use_safety_stock: true,
                recommend_param_customer_priority: false,
                recommend_param_include_plan_orders: true,
                recommend_param_include_unscheduled_orders: true,
                recommend_param_freeze_work_orders: true,
                recommend_param_freeze_shifts: 0,
                recommend_param_plan_after_frozen_orders: false,
                recommend_param_time_horizont_shifts: 2 * 21,
                recommend_param_unscheduled_offset_shifts: 2 * 21,
                recommend_param_meta_optimization: PLANNING_META_OPTIMIZATION.none,
                recommend_param_override_shifts: undefined,
                recommend_param_need_score_factor: 0,
                recommend_param_setup_code_change_factor:
                    LINE_GROUP_TAGS_ACCESS.planning_setup_code_change_factor(line_group_tags),
                recommend_param_work_duration_factor:
                    LINE_GROUP_TAGS_ACCESS.planning_work_duration_factor(line_group_tags),
                recommend_criteria_delays: 3,
                recommend_criteria_tool_changes: 2,
                recommend_criteria_intermediate_stock: 0,
                recommend_strategy: PLANNING_SCHEDULE_STRATEGY.BFS,
            },
            lines_orders,
            unscheduled_orders,
            planning_weeks: LINE_GROUP_TAGS_ACCESS.planning_table_weeks(line_group_tags),
            freeze_orders: [],
            plan_after_frozen_orders: false,
            order_quanity_produced_map: new Map(),
            order_time_start_map: new Map(),
            rearrange_orders_map: LinesOrdersLogic.rearrangeOrdersMap(
                original_lines_orders,
                original_unscheduled_orders
            ),
            original_lines_orders,
        };

        return parameters;
    };

    loadInitialPlanScore = async () => {
        this.props.reduxDispatch({
            type: SET_PENDING_INITIAL_REPORT_UUID,
            data: this.props.original_report_uuid,
        });
        const parameters = this.prepareRecommendPlanParams(true);
        const { plan_result, plan_uuid } = await RecommendPlanLogic.getPlanScore(parameters);
        this.props.reduxDispatch({
            type: UPDATE_INITIAL_RECOMMEND_PLAN,
            data:
                plan_result !== null
                    ? {
                          plan_uuid,
                          parameters,
                          plan_created_ts: new Date().getTime(),
                          score: plan_result.score,
                          stock_report_uuid: plan_result.stock_report_uuid,
                          report_uuid: this.props.original_report_uuid,
                      }
                    : null,
        });

        this.props.reduxDispatch({
            type: SET_PENDING_INITIAL_REPORT_UUID,
            data: "",
        });
    };

    loadCurrentPlanScore = async () => {
        this.props.reduxDispatch({
            type: SET_PENDING_CURRENT_REPORT_UUID,
            data: this.props.report_uuid,
        });
        const parameters = this.prepareRecommendPlanParams(false);
        const { plan_result, plan_uuid } = await RecommendPlanLogic.getPlanScore(parameters);
        if (this.props.report_uuid === this.props.pending_current_report_uuid) {
            this.props.reduxDispatch({
                type: UPDATE_CURRENT_RECOMMEND_PLAN,
                data:
                    plan_result !== null
                        ? {
                              plan_uuid,
                              parameters,
                              plan_created_ts: new Date().getTime(),
                              score: plan_result.score,
                              stock_report_uuid: plan_result.stock_report_uuid,
                              report_uuid: this.props.report_uuid,
                          }
                        : null,
            });
        }

        this.props.reduxDispatch({
            type: SET_PENDING_CURRENT_REPORT_UUID,
            data: "",
        });
    };

    trimKpis = (weeks: IWeekKPIs[]): IWeekKPIs[] => {
        if (weeks) {
            if (weeks.length > this.props.planning_weeks) {
                return weeks.slice(0, this.props.planning_weeks);
            }
            return weeks;
        }
        return [];
    };

    loadReport = async (report_uuid: string, is_initial?: boolean) => {
        const report_exists = this.props.kpis.some(k => k.report_uuid === report_uuid);
        if (report_exists) {
            return;
        }

        this.setState({ loading: true });
        const res = await getBackend2().reports.getReportKPIs({
            id: report_uuid,
            line_uuids: this.props.line_uuids,
        });

        if (res && res.weeks) {
            const date = new Date();
            this.props.reduxDispatch({
                type: ADD_KPI_ACTION_NAME,
                data: {
                    name: niceDateTime(date),
                    time_ts: date.getTime(),
                    is_initial,
                    report_uuid: report_uuid,
                    kpis: this.trimKpis(res.weeks),
                },
            });
        }
        this.setState({ loading: false });
    };

    isRecommendPlanReport = (report_uuid: string) => {
        return this.props.recommend_plans.some(k => k.report_uuid === report_uuid);
    };

    getOptions = (): ILabelValuePair[] => {
        const data: ILabelValuePair[] = this.props.recommend_plans
            .filter(recommend_plan => {
                // Will be added at the end
                const is_not_current_report = recommend_plan.report_uuid !== this.props.report_uuid;
                return recommend_plan.report_uuid && is_not_current_report;
            })
            .map(recommend_plan => ({
                value: recommend_plan.report_uuid,
                label: niceDateTime(new Date(recommend_plan.plan_created_ts)),
            }));

        if (!this.props.original_report_uuid) {
            return [];
        }

        const options = [
            {
                value: this.props.original_report_uuid,
                label: translate("common.initial_version", "Initial version"),
            },
            ...data,
        ];

        if (this.props.report_uuid !== this.props.original_report_uuid && this.props.report_uuid) {
            const is_recommend = this.isRecommendPlanReport(this.props.report_uuid);
            options.push({
                value: this.props.report_uuid,
                label: translate("common.current_version", "Current version") + (is_recommend ? " (A)" : ""),
            });
        }

        return options;
    };

    onDropdown2Change = (option: ILabelValuePair) => {
        this.setState({ dropdown2_value: option.value });
    };

    onDropdown1Change = (option: ILabelValuePair) => {
        this.setState({ dropdown1_value: option.value });
    };

    load = () => {
        const options = this.getOptions();

        // dropdown 1 holds current report
        if (options.length > 0 && this.state.dropdown1_value !== this.props.report_uuid) {
            this.setState({ dropdown1_value: this.props.report_uuid });
        }

        // dropdown 2 holds first report
        if (options.length > 0 && this.state.dropdown2_value !== this.props.original_report_uuid) {
            return this.setState({ dropdown2_value: this.props.original_report_uuid });
        }
    };

    getDropdownValue2 = () => {
        const options = this.getOptions();
        return options.find(o => o.value === this.state.dropdown2_value) || null;
    };

    getDropdownValue1 = () => {
        const options = this.getOptions();
        return options.find(o => o.value === this.state.dropdown1_value) || null;
    };

    renderKpiTable = () => {
        if (!(this.state.dropdown2_value && this.state.dropdown1_value)) {
            return null;
        }

        const base_kpi = this.props.kpis.find(k => k.report_uuid === this.state.dropdown2_value);
        const kpi = this.props.kpis.find(k => k.report_uuid === this.state.dropdown1_value);

        if (!base_kpi || !kpi) {
            return null;
        }

        return (
            <KpiTable
                is_loading={this.props.is_loading}
                base_kpi_sum={calcKpiSum(base_kpi.kpis)}
                kpi_sum={calcKpiSum(kpi.kpis)}
                kpis={kpi.kpis}
                base_kpis={base_kpi.kpis}
            />
        );
    };

    renderScoresTable = () => {
        const is_initial_selected1 = this.state.dropdown1_value === this.props.original_report_uuid;
        const is_initial_selected2 = this.state.dropdown2_value === this.props.original_report_uuid;
        const is_initial_selected = is_initial_selected1 || is_initial_selected2;
        const is_initial_loading = this.props.pending_initial_report_uuid === this.props.original_report_uuid;
        const is_current_selected1 = this.state.dropdown1_value === this.props.report_uuid;
        const is_current_selected2 = this.state.dropdown2_value === this.props.report_uuid;
        const is_current_selected = is_current_selected1 || is_current_selected2;
        const is_current_loading = this.props.pending_current_report_uuid === this.props.report_uuid;
        const is_loading = (is_initial_selected && is_initial_loading) || (is_current_selected && is_current_loading);

        if (is_loading) {
            return <Loader small={true}></Loader>;
        }

        if (
            is_current_selected &&
            this.props.report_uuid !== this.props.original_report_uuid &&
            !this.isRecommendPlanReport(this.props.report_uuid) &&
            ((this.props.current_recommend_plan == null && this.props.pending_current_report_uuid === "") ||
                (this.props.current_recommend_plan != null &&
                    this.props.current_recommend_plan.report_uuid !== this.props.report_uuid &&
                    this.props.pending_current_report_uuid !== this.props.report_uuid))
        ) {
            return (
                <button className="btn btn-primary" onClick={this.loadCurrentPlanScore}>
                    {translate(
                        "Manufacturing.Planning.calculate_score_for_current_plan",
                        "Calculate score for current plan"
                    )}
                </button>
            );
        }

        const all_recommend_plans = [
            this.props.initial_recommend_plan,
            this.props.current_recommend_plan,
            ...this.props.recommend_plans,
        ];

        let plan_score: IPlanScore | undefined;
        let base_plan_score: IPlanScore | undefined;
        for (const recommend_plan of all_recommend_plans) {
            if (recommend_plan != null && recommend_plan.report_uuid === this.state.dropdown1_value) {
                plan_score = recommend_plan?.score;
            }

            if (recommend_plan != null && recommend_plan.report_uuid === this.state.dropdown2_value) {
                base_plan_score = recommend_plan?.score;
            }
        }

        return <PlanScoresTable plan_score={plan_score} base_plan_score={base_plan_score} />;
    };

    onOpenInfoModal = (option: ILabelValuePair) => () => {
        const recommend_plan = this.props.recommend_plans.find(rp => rp.report_uuid === option.value);
        if (!recommend_plan) {
            return null;
        }

        this.props.reduxDispatch({
            type: SET_RECOMMEND_PLAN_MODAL,
            data: {
                show_recommend_param_dialog: true,
                recommend_plan_params: recommend_plan.parameters.state,
            },
        });
    };

    onCloseModal = () => {
        this.setState({ show_modal: false, parameters: null });
    };

    render() {
        const options = this.getOptions();
        const dropdown2_value = this.getDropdownValue2();
        const dropdown1_value = this.getDropdownValue1();

        let is_dropdown1_recommend = false;
        if (dropdown1_value) {
            is_dropdown1_recommend = this.isRecommendPlanReport(dropdown1_value.value);
        }

        let is_dropdown2_recommend = false;
        if (dropdown2_value) {
            is_dropdown2_recommend = this.isRecommendPlanReport(dropdown2_value.value);
        }

        return (
            <div>
                <div className="kpis-container-dropdowns">
                    <div className="dropdown1">
                        <button
                            id="save-buttons-container"
                            className="btn btn-outline-primary btn-blue"
                            onClick={dropdown1_value && this.onOpenInfoModal(dropdown1_value)}
                            type="button"
                            aria-expanded="false"
                            disabled={!is_dropdown1_recommend}
                        >
                            <i className="fas fa-users-cog"></i>
                        </button>
                        <Select
                            id="kpis-dropdown1"
                            value={dropdown1_value}
                            options={options}
                            onChange={this.onDropdown1Change}
                        />
                    </div>
                    <span style={{ display: "flex", alignSelf: "center", marginLeft: "10px" }}>/</span>
                    <div className="dropdown2">
                        <Select
                            id="kpis-dropdown2"
                            value={dropdown2_value}
                            options={options}
                            onChange={this.onDropdown2Change}
                        />
                        <button
                            id="save-buttons-container"
                            onClick={dropdown2_value && this.onOpenInfoModal(dropdown2_value)}
                            type="button"
                            className="btn btn-outline-primary btn-blue"
                            aria-haspopup="true"
                            aria-expanded="false"
                            style={{ marginLeft: "10px" }}
                            disabled={!is_dropdown2_recommend}
                        >
                            <i className="fas fa-users-cog"></i>
                        </button>
                    </div>
                </div>
                <div className="p-2">
                    <h2 className="h4">{translate("common.kpis", "KPIs")}</h2>
                    {this.renderKpiTable()}
                </div>
                <div className="p-2">
                    <h2 className="h4">{translate("common.score", "Score")}</h2>
                    {this.renderScoresTable()}
                </div>
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const kpis = state.gantt_chart_kpis.kpis;
    // We show same number of weeks as on the planning board
    const planning_weeks = Math.floor(state.gantt_chart_properties.days / 7) ?? 0;
    // We use original report as base
    const original_report_uuid = state.gantt_chart_report.original_report_uuid ?? "";
    // List of lines we are planning so we focus with KPIs only on those
    const line_uuids = state.gantt_chart_lines_orders.lines_orders.map(lo => lo.line_uuid);
    const recommend_plans = state.gantt_chart_recommend_plans.recommend_plans;
    const report_uuid =
        state.gantt_chart_report.current_report_uuid || state.gantt_chart_report.original_report_uuid || "";
    const current_recommend_plan = state.gantt_chart_recommend_plans.current_recommend_plan;
    const initial_recommend_plan = state.gantt_chart_recommend_plans.initial_recommend_plan;
    const pending_current_report_uuid = state.gantt_chart_recommend_plans.pending_current_report_uuid;
    const pending_initial_report_uuid = state.gantt_chart_recommend_plans.pending_initial_report_uuid;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const unscheduled_orders = state.gantt_chart_lines_orders.unscheduled_orders;
    const original_lines_orders = state.gantt_chart_lines_orders.original_lines_orders ?? [];
    const original_unscheduled_orders = state.gantt_chart_lines_orders.original_unscheduled_orders;
    const selected_line_uuids = state.gantt_chart_filters.selected_line_uuids;
    const properties = state.gantt_chart_properties;
    const line_group_uuid = state.gantt_chart_properties.line_group_uuid ?? "";

    return {
        kpis,
        planning_weeks,
        line_uuids,
        original_report_uuid,
        recommend_plans,
        report_uuid,
        current_recommend_plan,
        initial_recommend_plan,
        pending_current_report_uuid,
        pending_initial_report_uuid,
        line_group_uuid,
        lines_orders,
        unscheduled_orders,
        original_lines_orders,
        original_unscheduled_orders,
        selected_line_uuids,
        properties,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const ConnectedKpisContainer = connect<
    ReturnType<typeof mapState>,
    ReturnType<typeof mapDispatch>,
    OwnProps,
    ReduxState
>(
    mapState,
    mapDispatch
)(KpisContainer);

export default ConnectedKpisContainer;
